import { db } from './firebase.js';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';

export const CaseService = {
    // Crear un nuevo expediente
    async createCase(caseData) {
        try {
            // Estructura sugerida:
            // {
            //   title: "Accion de Inconstitucionalidad vs Ley X",
            //   client: "Juan Pérez",
            //   status: "Abierto",
            //   notebookLMUrl: "https://notebooklm.google.com/notebook/...",
            //   ethicalWall: ["user_id_1", "user_id_2"],
            //   parentId: "case_id_principal" (Opcional, si es sub-expediente)
            //   createdAt: new Date()
            // }
            const caseWithExtensions = {
                ...caseData,
                status: caseData.status || 'active',
                parentId: caseData.parentId || null, // null = Expediente Principal
                metadata: {
                    priority: 'medium',
                    source: 'manual_entry'
                },
                tags: [],
                customFields: {},
                extensions: {
                    ai_analytics: {},
                    workflow_state: 'registration'
                }
            };
            const docRef = await addDoc(collection(db, "cases"), caseWithExtensions);
            return docRef.id;
        } catch (e) {
            console.error("Error agregando caso: ", e);
            throw e;
        }
    },

    // Obtener casos autorizados para el usuario actual (Abogado o Cliente)
    async getMyCases(userId, role) {
        try {
            let q;
            if (role === 'cliente') {
                q = query(collection(db, "cases"), where("clientUid", "==", userId));
            } else if (role === 'abogado_titular') {
                // El abogado titular ve TODO (Modo Dios)
                // Se podría ordenar por fecha de creación descendente si existe el índice
                // q = query(collection(db, "cases"), orderBy("createdAt", "desc"));
                // Por ahora, sin orden para evitar errores de índice faltante
                q = query(collection(db, "cases"));
            } else {
                // Staff / Asociados: Solo lo que se les ha compartido (Muro Ético)
                q = query(collection(db, "cases"), where("ethicalWall", "array-contains", userId));
            }
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (e) {
            console.error("Error obteniendo casos: ", e);
            throw e;
        }
    },

    // Obtener un caso específico por ID
    async getCaseById(caseId) {
        try {
            const { doc, getDoc } = await import('firebase/firestore');
            const docRef = doc(db, "cases", caseId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() };
            } else {
                throw new Error("El expediente no existe.");
            }
        } catch (e) {
            console.error("Error obteniendo caso:", e);
            throw e;
        }
    },

    // Obtener sub-expedientes (incidentes, medidas, etc.) vinculados a un caso principal
    async getSubCases(parentId) {
        try {
            const q = query(collection(db, "cases"), where("parentId", "==", parentId));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (e) {
            console.error("Error obteniendo sub-expedientes: ", e);
            throw e;
        }
    },

    // Agregar actuación procesal
    async addAction(caseId, actionData) {
        try {
            const { runTransaction, doc } = await import('firebase/firestore');

            // Usamos una transacción para asegurar que el índice del folio sea único y secuencial
            const caseRef = doc(db, "cases", caseId);

            await runTransaction(db, async (transaction) => {
                const caseDoc = await transaction.get(caseRef);
                if (!caseDoc.exists()) throw "Caso no encontrado";

                let nextFolio = (caseDoc.data().lastDocIndex || 0);

                // Si la actuación incluye un archivo, incrementamos el folio
                if (actionData.driveFileId) {
                    nextFolio += 1;
                    actionData.folioNumber = nextFolio;
                    transaction.update(caseRef, { lastDocIndex: nextFolio });
                }

                const actionsRef = collection(db, "cases", caseId, "actions");
                const newActionDoc = doc(actionsRef);
                transaction.set(newActionDoc, {
                    ...actionData,
                    timestamp: new Date(),
                    uploadTime: new Date(), // Persist original upload time for explicit display
                    metadata: actionData.metadata || {},
                    attachments: actionData.attachments || []
                });
            });

            return newActionDoc.id;
        } catch (e) {
            console.error("Error agregando actuación:", e);
            throw e;
        }
    },

    // Obtener historial de actuaciones
    async getActions(caseId) {
        try {
            const { orderBy } = await import('firebase/firestore');
            const actionsRef = collection(db, "cases", caseId, "actions");
            const q = query(actionsRef, orderBy("timestamp", "desc"));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (e) {
            console.error("Error obteniendo actuaciones:", e);
            throw e;
        }
    },

    // Actualizar una actuación existente
    async updateAction(caseId, actionId, updateData) {
        try {
            const { doc, updateDoc } = await import('firebase/firestore');
            const actionRef = doc(db, "cases", caseId, "actions", actionId);
            await updateDoc(actionRef, updateData);
            return true;
        } catch (e) {
            console.error("Error actualizando actuación:", e);
            throw e;
        }
    },

    // Eliminar una actuación
    async deleteAction(caseId, actionId) {
        try {
            const { doc, deleteDoc } = await import('firebase/firestore');
            const actionRef = doc(db, "cases", caseId, "actions", actionId);
            await deleteDoc(actionRef);
            return true;
        } catch (e) {
            console.error("Error eliminando actuación:", e);
            throw e;
        }
    },


    // Verificar si un archivo ya existe en el caso (Evitar Duplicados)
    async checkDuplicateFile(caseId, fileName) {
        try {
            const { getDocs, query, where, collection } = await import('firebase/firestore');
            const q = query(
                collection(db, "cases", caseId, "actions"),
                where("driveFileName", "==", fileName)
            );
            const snapshot = await getDocs(q);
            return !snapshot.empty;
        } catch (e) {
            console.error("Error verificando duplicado:", e);
            return false;
        }
    },

    // --- Sub-módulo: Redacción Procesal (writings) ---

    async getWritings(caseId) {
        try {
            const { orderBy } = await import('firebase/firestore');
            // Assuming we will store writings in a subcollection "writings"
            // If doesn't exist yet, it returns empty
            const writingsRef = collection(db, "cases", caseId, "writings");
            // Order by timestamp descending
            const q = query(writingsRef);
            // Note: If you need explicit ordering, ensure you have an index or use client-side sorting if small.
            // Let's rely on client sort for MVP or default creation order.
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => b.updatedAt - a.updatedAt);
        } catch (e) {
            console.error("Error obteniendo escritos:", e);
            return [];
        }
    },

    async addWriting(caseId, data) {
        try {
            const writingsRef = collection(db, "cases", caseId, "writings");
            const docRef = await addDoc(writingsRef, {
                ...data,
                createdAt: new Date(),
                updatedAt: new Date(),
                status: data.status || 'DRAFT' // DRAFT, REVIEW, SIGNED, FILED
            });
            return docRef.id;
        } catch (e) {
            console.error("Error creando escrito:", e);
            throw e;
        }
    },

    async updateWriting(caseId, writingId, data) {
        try {
            const { doc, updateDoc } = await import('firebase/firestore');
            const ref = doc(db, "cases", caseId, "writings", writingId);
            await updateDoc(ref, {
                ...data,
                updatedAt: new Date()
            });
        } catch (e) {
            console.error("Error actualizando escrito:", e);
            throw e;
        }
    },

    // Obtener el siguiente número de folio disponible
    async getNextFolioNumber(caseId) {
        const { doc, getDoc } = await import('firebase/firestore');
        const caseSnap = await getDoc(doc(db, "cases", caseId));
        return (caseSnap.data()?.lastDocIndex || 0) + 1;
    },

    // Obtener Agenda Global (Plazos de todos los casos)
    async getUpcomingDeadlines(userId) {
        // 1. Obtener casos del usuario
        const myCases = await this.getMyCases(userId);
        let allDeadlines = [];

        // 2. Para cada caso, buscar actuaciones marcadas como deadline
        // Nota: En producción esto debería ser una Collection Group Query indexada
        for (const c of myCases) {
            const actions = await this.getActions(c.id);
            const caseDeadlines = actions
                .filter(a => a.eventDateTime || a.isDeadline) // Filtrar solo eventos con fecha
                .map(a => ({
                    ...a,
                    caseTitle: c.title,
                    caseId: c.id,
                    judicialId: c.judicialId || 'S/N',
                    dateObj: new Date(a.eventDateTime || a.timestamp.seconds * 1000)
                }));
            allDeadlines = [...allDeadlines, ...caseDeadlines];
        }

        // 3. Ordenar por fecha próxima
        return allDeadlines
            .filter(d => d.dateObj >= new Date(new Date().setHours(0, 0, 0, 0))) // Solo futuros o de hoy
            .sort((a, b) => a.dateObj - b.dateObj);
    }
};
